<template>
  <q-page>
    <div class="row">
      <div class="col-12">
        <q-card>
          <q-card-section>
            <div class="row items-center">
              <div style="display: inline-block" class="text-h6">Entries</div>
              <q-space />
              <q-btn
                class="q-ml-md"
                color="secondaryButton"
                text-color="accent"
                no-caps
                unelevated
                icon="mdi-download"
                dense
                flat
                v-if="currentUserData.role != 'Official'"
                @click="downloads('CompetitionEntriesSummary')"
              ></q-btn>

              <q-btn
                class="q-ml-md"
                color="secondaryButton"
                text-color="accent"
                no-caps
                unelevated
                icon="mdi-filter-outline"
                @click="filterDialog = true"
              >
              </q-btn>
            </div>
          </q-card-section>

          <q-separator />
          <q-card-section class="q-pa-none">
            <div v-if="gridData">
              <div class="row">
                <div
                  class="col-12 mt-0 pt-0"
                  style="height: calc(100vh - 120px) !important"
                >
                  <datatable
                    :filter="filter"
                    :flat="true"
                    :externalFilter="externalFilter"
                    :data="gridData"
                    :columns="columns"
                    @rowDoubleClick="handleDoubleClick"
                  >
                    <template slot="body-cell-nfc" slot-scope="data">
                      <q-td :props="data.props">
                        <q-icon
                          name="mdi-nfc-variant"
                          class="cursor-pointer"
                          :class="{
                            'text-positive':
                              data.props.row.nfcTags &&
                              data.props.row.nfcTags.length > 0
                          }"
                          @click="startNFCScanner(data.props.row)"
                          size="sm"
                        >
                        </q-icon>
                      </q-td>
                    </template>
                    <template slot="body-cell-capUrl" slot-scope="data">
                      <q-td :props="data.props">
                        <q-img
                          v-if="data.props.row.capUrl"
                          :src="data.props.row.capUrl"
                          style="width: 40px"
                          contain
                          class="q-ml-sm float-right"
                        >
                        </q-img>
                      </q-td>
                    </template>
                  </datatable>
                </div>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>
    <q-dialog v-model="filterDialog" persistent max-width="600px">
      <q-card v-if="filterDialog" tile style="width: 600px; max-width: 600px">
        <q-card-section class="q-pa-sm q-pl-md">
          <div class="row items-center">
            <div style="display: inline-block" class="text-h6">Filter</div>
            <q-space />
            <q-btn round flat icon="mdi-close" @click="filterDialog = false">
            </q-btn>
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section class="q-pa-none">
          <div class="row q-pa-md">
            <div class="col-12 q-mb-sm">
              <autocomplete
                label="Filter by Division"
                dense
                :items="divisions"
                :itemsLoading="divisionsLoading"
                option_label="division_name"
                option_value="id"
                :id="filter.division_id"
                @changed="val => (filter.division_id = val)"
                clearable
              />
            </div>
            <div class="col-12">
              <q-input
                filled
                v-model="filter.search"
                debounce="200"
                label="Search"
                clearable
                dense
              >
                <template #prepend>
                  <q-icon name="mdi-magnify"> </q-icon>
                </template>
              </q-input>
            </div>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script>
import { mapGetters } from "vuex";
import datatable from "@/components/datatable/datatable.vue";
import moment from "moment";
import autocomplete from "../../components/wrappers/autocomplete/autocomplete.vue";
import api from "@/api.js";
import { NFCScan, NFCWrite } from "@/plugins/NFC";

// import { Notify } from "quasar";

export default {
  components: {
    datatable,
    autocomplete
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
      currentUserData: "auth/currentUserData",
      gridData: "competitions/entriesList",
      dataLoading: "competitions/entriesListLoading",
      divisions: "divisions/list",
      divisionsLoading: "divisions/listLoading"
    }),
    columns: function() {
      return [
        {
          name: "nfc",
          label: "NFC",
          align: "left",
          field: "nfc",
          style:
            "min-width: 80px;max-width: 80px;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;"
        },
        {
          name: "name",
          label: "Name",
          align: "left",
          field: "firstName",
          format: (val, row) => `${row.firstName} ${row.lastName}`,
          style:
            "min-width: 150px;max-width: 150px;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;",
          sortable: true
        },
        {
          name: "lastName",
          label: "Last Name",
          align: "left",
          field: "lastName",
          style:
            "min-width: 150px;max-width: 150px;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;",
          classes: "hidden",
          headerClasses: "hidden"
        },
        {
          name: "capUrl",
          label: "",
          align: "left",
          field: "capUrl",
          style:
            "min-width: 80px;max-width: 80px; width: 80px;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;"
        },
        {
          name: "association_name",
          label: "Club",
          align: "left",
          field: "association_name",
          style:
            "min-width: 150px;max-width: 150px;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;",
          sortable: true
        },
        {
          name: "birthDate",
          label: "DOB",
          align: "left",
          field: "birthDate",
          style:
            "min-width: 150px;max-width: 150px;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;",
          classes: this.currentUserData.role == "Official" ? "hidden" : "",
          headerClasses:
            this.currentUserData.role == "Official" ? "hidden" : "",
          sortable: true
        },
        {
          name: "gender",
          label: "Gender",
          align: "left",
          field: "gender",
          style:
            "min-width: 150px;max-width: 150px;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;",

          sortable: true
        },
        {
          name: "division",
          label: "Division",
          align: "left",
          field: "birthDate",
          format: (val, row) => {
            if (this.divisions) {
              let year = moment().format("YYYY");
              let reference = moment(`${year}-09-30`);
              if (moment(`${year}-07-01`) > moment()) {
                reference = reference.subtract(1, "years");
              }
              let age = reference.diff(val, "years", false);
              let div = this.divisions.find(
                d =>
                  d.division_age_min <= age &&
                  d.age_max >= age &&
                  row.gender == d.gender
              );
              return div ? div.division_name : "No Division";
            } else {
              return "";
            }
          },
          style:
            "min-width: 150px;max-width: 150px;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;",
          sort: (a, b, rowA, rowB) => {
            let year = moment().format("YYYY");
            let reference = moment(`${year}-09-30`);
            if (moment(`${year}-07-01`) > moment()) {
              reference = reference.subtract(1, "years");
            }

            let ageA = reference.diff(rowA.birthDate, "years", false);
            let ageB = reference.diff(rowB.birthDate, "years", false);

            if (ageA == ageB) {
              return rowA.gender.localeCompare(rowB.gender);
            } else {
              return ageA - ageB;
            }
          },
          sortable: true
        },
        {
          name: "emailAddress",
          label: "Email",
          align: "left",
          field: "emailAddress",
          style:
            "min-width: 150px;max-width: 150px;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;",
          classes: this.currentUserData.role == "Official" ? "hidden" : "",
          headerClasses:
            this.currentUserData.role == "Official" ? "hidden" : "",
          sortable: true
        },
        {
          name: "mobilePhone",
          label: "Mobile Phone",
          align: "left",
          field: "mobilePhone",
          style:
            "min-width: 150px;max-width: 150px;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;",
          classes: this.currentUserData.role == "Official" ? "hidden" : "",
          headerClasses:
            this.currentUserData.role == "Official" ? "hidden" : "",
          sortable: true
        }
        // {
        //   name: "actions",
        //   label: "Actions",
        //   align: "left",
        //   sortable: false,
        // },
      ];
    }
  },
  data: () => ({
    // data-table

    archivingCompetitionID: null,

    // Filter
    filter: {
      search: "",
      division_id: null
    },
    filterDialog: false,

    nfcScanner: null,

    ndef: null,
    handleReading: null
  }),
  watch: {
    $route: function() {
      this.handleRoute();
    }
  },
  mounted() {
    this.loadData();
    this.$store.dispatch("divisions/loadAll");
  },
  methods: {
    handleDoubleClick(row) {
      // this.$router.push(`${this.$route.path}/${row.id}`);
    },
    loadData() {
      this.$store.dispatch(
        "competitions/loadAllEntries",
        this.$route.params.competitions_id
      );
    },
    // Grid Filter
    externalFilter(rows, terms, cols) {
      let year = moment().format("YYYY");
      let reference = moment(`${year}-09-30`);
      if (moment(`${year}-07-01`) > moment()) {
        reference = reference.subtract(1, "years");
      }

      return rows.filter(row => {
        let passed = 0;

        // Text Search
        if (this.filter.search && this.filter.search != "") {
          for (let i = 0; i < cols.length; i++) {
            let val = `${row[cols[i].field]}`.toLowerCase();
            passed +=
              val.toLowerCase().indexOf(this.filter.search.toLowerCase()) > -1
                ? 1
                : 0;

            if (passed == 1) {
              i = cols.length;
            }
          }
        } else {
          passed += 1;
        }

        // Division Filter
        if (this.filter.division_id) {
          let age = reference.diff(row.birthDate, "years", false);
          let div = this.divisions.find(
            d =>
              d.division_age_min <= age &&
              d.age_max >= age &&
              row.gender == d.gender
          );

          if (this.filter.division_id == div.id) {
            passed += 1;
          } else {
            return false;
          }
        } else {
          passed += 1;
        }

        if (passed == 2) {
          return true;
        } else {
          return false;
        }
      });
    },
    downloads(type) {
      switch (type) {
        case "CompetitionEntriesSummary":
          window.open(
            `https://api.live.simplesoftware.nz/waves/report/download?competitions_id=${this.$route.params.competitions_id}&type=CompetitionEntriesTableMirror`,
            "_blank"
          );
          break;

        default:
          break;
      }
    },

    async handleNFCScan(message, serialNumber, extraData) {
      this.ndef.removeEventListener("reading", this.handleReading);
      console.log(this.nfcScanner);

      // ADD NFC to Member
      try {
        let response = await this.$store.dispatch(
          "associations/addNFCToMember",
          {
            id: extraData.id,
            serialNumber: serialNumber,
            associations_id: extraData.associations_id,
            skipLoading: true
          }
        );

        this.loadData();

        if (!response.Response.Result) {
          this.$q.notify({
            position: "top-right",
            type: response.Response.Result ? "positive" : "negative",
            message: response.Response.Message
          });
        }
      } catch (error) {
        this.$q.notify({
          position: "top-right",
          type: "negative",
          message: "There was an error, Please try again later"
        });
      }
    },
    async startNFCScanner(row) {
      const { ndef, handleReading } = await NFCScan(
        this,
        this.handleNFCScan,
        row
      );
      this.ndef = ndef;
      this.handleReading = handleReading;

      NFCWrite(
        this,
        `${row.firstName} ${row.lastName} - ${row.id} - ${row.crm_id} - ${row.association_name}`
      );
    }
  }
};
</script>

<style lang="scss" scoped>
#depthCard:hover {
  background: #5cb3ff;
  color: white;
}

.formHeight {
  min-height: calc(100vh - 175px);
  max-height: 1000px;
}
</style>
